import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'rider' | 'co-rider' | 'manager';
  approved: boolean;
  spender?: boolean; // Neues Feld
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: Partial<User>) => void;
  user: User | null;
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  user
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'rider' as 'rider' | 'co-rider' | 'manager',
    approved: false,
    spender: false // Neues Feld initialisiert
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = isMobile ? 'hidden' : 'auto';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'rider',
        approved: user.approved || false,
        spender: user.spender || false // Initialisieren des Spender-Feldes
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'rider',
        approved: false,
        spender: false // Zurücksetzen beim Erstellen eines neuen Benutzers
      });
    }
    console.log('FormData initialized:', formData);
  }, [user, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto">
      <div className={`
        min-h-full
        ${isMobile ? '' : 'py-6'}
        flex items-center justify-center
      `}>
        <div className={`
          w-full
          ${isMobile ? 'min-h-screen' : 'max-h-[calc(100vh-3rem)]'}
          ${isMobile ? '' : 'max-w-2xl'}
          bg-white dark:bg-gray-800
          ${isMobile ? '' : 'rounded-lg shadow-xl'}
          flex flex-col
        `}>
          <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {user ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1">
            <div className={`
              flex-1 p-4 overflow-y-auto
              ${isMobile ? 'max-h-[calc(100vh-8rem)]' : 'max-h-[calc(100vh-12rem)]'}
            `}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">E-Mail</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rolle</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'rider' | 'co-rider' | 'manager' })}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="rider">Reiter (Pferdebesitzer)</option>
                    <option value="co-rider">Mitreiter</option>
                    <option value="manager">Verwalter</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="approved"
                    checked={formData.approved}
                    onChange={(e) => setFormData({ ...formData, approved: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                  />
                  <label htmlFor="approved" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Bestätigt
                  </label>
                </div>

                {/* Neues Spender-Feld immer anzeigen */}
                <div className="flex items-center" style={{ backgroundColor: 'yellow' }}> {/* Temporäre Hintergrundfarbe für Debugging */}
                  <input
                    type="checkbox"
                    id="spender"
                    checked={formData.spender}
                    onChange={(e) => setFormData({ ...formData, spender: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                  />
                  <label htmlFor="spender" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                    Spender
                  </label>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors"
                >
                  {user ? 'Änderungen speichern' : 'Benutzer hinzufügen'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
